/**
 * JWT tamper detection.
 *
 * On app load, validates that the stored Supabase JWT is structurally sound.
 * If the token is malformed (tampered or corrupted), it clears storage and
 * redirects to the login page.
 *
 * Edge case: EC-AUTH-005 — invalid JWT signature → immediate logout.
 */
import { isInvalidRefreshTokenError } from "./authErrors";

// ─── Minimal Supabase interface ─────────────────────────────────────────────
interface MinimalSupabase {
  auth: {
    getSession: () => PromiseLike<{
      data: { session: { access_token: string } | null };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error: any;
    }>;
    signOut: () => PromiseLike<unknown>;
  };
}

/** Decode a base64url string (JWT segment). Returns null on failure. */
function decodeBase64Url(segment: string): string | null {
  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return atob(padded);
  } catch {
    return null;
  }
}

/** Check that a JWT has valid structure (3 dot-separated base64url parts). */
function isValidJwtStructure(token: string): boolean {
  const parts = token.split(".");
  if (parts.length !== 3) return false;

  // Header and payload must be decodable JSON
  const header = decodeBase64Url(parts[0]);
  const payload = decodeBase64Url(parts[1]);
  if (!header || !payload) return false;

  try {
    const h = JSON.parse(header);
    const p = JSON.parse(payload);
    // Minimal checks: header has alg, payload has exp (structural only — expiry is Supabase's job)
    if (!h.alg) return false;
    if (typeof p.exp !== "number") return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate the current JWT on app load.
 *
 * @param supabase - Supabase client
 * @param loginPath - Path to redirect to on tamper detection (default: "/login")
 * @param onTamperDetected - Optional callback before redirect (e.g. to log audit event)
 * @returns `true` if session is valid or absent, `false` if tampered (triggers redirect)
 */
export async function validateJwtOnLoad(
  supabase: MinimalSupabase,
  loginPath = "/login",
  onTamperDetected?: () => Promise<void>,
): Promise<boolean> {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    // No session = not logged in, that's fine
    if (!session) return true;

    // Supabase returned an error (e.g. network failure, token refresh failure).
    if (error) {
      if (isInvalidRefreshTokenError(error)) {
        await clearAndRedirect(supabase, loginPath, onTamperDetected);
        return false;
      }
      console.warn("[jwt] Session fetch error (skipping tamper check):", error);
      return true;
    }

    // Validate JWT structure — only sign out on actual structural tampering
    if (!isValidJwtStructure(session.access_token)) {
      console.warn("[jwt] Tampered or malformed JWT detected");
      await clearAndRedirect(supabase, loginPath, onTamperDetected);
      return false;
    }

    return true;
  } catch (err) {
    // Don't sign out on unexpected errors — could be a transient network issue
    console.warn("[jwt] JWT validation error (skipping):", err);
    return true;
  }
}

async function clearAndRedirect(
  supabase: MinimalSupabase,
  loginPath: string,
  onTamperDetected?: () => Promise<void>,
): Promise<void> {
  if (onTamperDetected) {
    try {
      await onTamperDetected();
    } catch {
      // Best effort
    }
  }

  try {
    await supabase.auth.signOut();
  } catch {
    // Best effort
  }

  try {
    localStorage.clear();
    sessionStorage.clear();
    // Clear any Supabase auth cookies (chunked or plain)
    // Supabase uses "sb-" prefix: "sb-venuehub_consumer_token-auth-token"
    document.cookie.split("; ").forEach((c) => {
      const name = c.split("=")[0];
      if ((name.startsWith("venuehub_") || name.startsWith("sb-venuehub_")) && name.includes("token")) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Strict`;
      }
    });
  } catch {
    // Storage might be restricted
  }

  if (typeof window !== "undefined") {
    window.location.href = loginPath;
  }
}
